import { useState, useEffect } from "react";
import {
  Link,
  useLoaderData,
  useSearchParams,
  useParams,
} from "react-router-dom";

export interface FormStep {
  question: string;
  answers: Array<
    | { answer: string; form: FormStep }
    | { answer: string; recommendation: string }
  >;
}

interface FormButtonProps {
  answer: string;
  onClickHandler(): void;
}

function FormButton({ answer, onClickHandler: handleClick }: FormButtonProps) {
  return (
    <button
      onClick={handleClick}
      className="btn mt-3 mr-3 last:mr-0 rounded-sm"
    >
      {answer}
    </button>
  );
}

function FormStep({ question, answers }: FormStep) {
  const [questions, setQuestions] = useState([question]);
  const [currentAnswers, setCurrentAnswers] = useState([answers]);
  const [recommendation, setRecommendation] = useState("");

  async function finalButtonHandler(recommendation: string) {
    setRecommendation(recommendation);
  }

  function intermediateButtonHandler(formStep: FormStep) {
    setQuestions((prev) => [...prev, formStep.question]);
    setCurrentAnswers((prev) => [...prev, formStep.answers]);
  }

  return (
    <>
      {recommendation ? (
        <>
          <h1 className="text-2xl mb-2 underline">Treatment Details</h1>
          <p className="whitespace-pre-wrap">{recommendation}</p>
        </>
      ) : (
        <>
          <p className="whitespace-pre-wrap">
            {questions[questions.length - 1]}
          </p>
          <div>
            {currentAnswers[currentAnswers.length - 1].map((answer, index) => {
              return "recommendation" in answer ? (
                <FormButton
                  key={index}
                  answer={answer.answer}
                  onClickHandler={() =>
                    finalButtonHandler(answer.recommendation)
                  }
                />
              ) : (
                <FormButton
                  key={index}
                  answer={answer.answer}
                  onClickHandler={() => intermediateButtonHandler(answer.form)}
                />
              );
            })}
          </div>
        </>
      )}

      <div>
        <button
          onClick={() => {
            setQuestions((prev) => prev.slice(0, 1));
            setCurrentAnswers((prev) => prev.slice(0, 1));
            setRecommendation("");
          }}
          className="btn mt-3 mr-3 rounded-sm"
          disabled={questions.length < 2 && !recommendation}
        >
          Reset Algorithm
        </button>
        <button
          onClick={() => {
            if (!recommendation) {
              // https://react.dev/learn/updating-arrays-in-state#removing-from-an-array
              setQuestions((prev) => prev.slice(0, -1));
              setCurrentAnswers((prev) => prev.slice(0, -1));
            } else {
              setRecommendation("");
            }
          }}
          className="btn mt-3 rounded-sm"
          disabled={questions.length < 2 && !recommendation}
        >
          Back
        </button>
      </div>
    </>
  );
}

function InteractiveForm() {
  const { algorithm, title } = useLoaderData() as {
    algorithm: FormStep;
    title: string;
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const { algorithmId } = useParams();
  const isDemo = Boolean(searchParams.get("demo")) || false;
  useEffect(() => {
    if (isDemo) {
      const htmlElement = document.querySelector("html")!;
      const previousTheme = htmlElement.getAttribute("data-theme");
      htmlElement.setAttribute("data-theme", "light");
      return () => {
        if (previousTheme) {
          htmlElement.setAttribute("data-theme", previousTheme);
        } else {
          htmlElement.removeAttribute("data-theme");
        }
      };
    }
  }, [isDemo]);
  return (
    <>
      <h1 className="text-3xl font-bold mb-6">{title}</h1>
      <FormStep question={algorithm.question} answers={algorithm.answers} />
      {!isDemo && (
        <>
          <Link to={"/"} className="btn btn-link">
            Home
          </Link>
          {typeof algorithmId === "string" ? (
            <Link
              to={`/algorithm-builder/${algorithmId}`}
              className="btn btn-link"
            >
              Edit with Form Builder
            </Link>
          ) : (
            <button
              className="btn btn-link"
              onClick={() =>
                setSearchParams((prev) => ({ ...prev, demo: true }))
              }
            >
              View as Demo
            </button>
          )}
        </>
      )}
    </>
  );
}

export default InteractiveForm;
